import React, { useState } from 'react';
import { 
  X, 
  User, 
  Building2, 
  Mail, 
  Phone, 
  MapPin, 
  CreditCard, 
  Loader2,
  CheckCircle2,
  AlertCircle,
  MessageCircle,
  Info
} from 'lucide-react';
import { Button } from './ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const CheckoutModal = ({ isOpen, onClose, cart, total, onSuccess }) => {
  const [step, setStep] = useState(1); // 1 = tipo, 2 = dados, 3 = processando
  const [personType, setPersonType] = useState(''); // 'cpf' ou 'cnpj'
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Dados do formulário
  const [formData, setFormData] = useState({
    // CPF
    cpf: '',
    data_nascimento: '',
    nome_completo: '',
    // CNPJ
    cnpj: '',
    razao_social: '',
    nome_fantasia: '',
    tipo_empresa: '',
    // Contato
    email: '',
    celular: '',
    telefone: '',
    whatsapp: '',
    // Endereço
    cep: '',
    logradouro: '',
    numero: '',
    complemento: '',
    bairro: '',
    cidade: '',
    estado: ''
  });

  const tiposEmpresa = [
    { id: 'oficina_mecanica', label: 'Oficina Mecânica' },
    { id: 'oficina_celular', label: 'Oficina de Celular' },
    { id: 'oficina_moto', label: 'Oficina de Moto' },
    { id: 'outros', label: 'Outros' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  // Formatar CPF: 000.000.000-00
  const formatCPF = (value) => {
    const numbers = value.replace(/\D/g, '').slice(0, 11);
    return numbers
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,2})/, '$1-$2');
  };

  // Formatar CNPJ: 00.000.000/0000-00
  const formatCNPJ = (value) => {
    const numbers = value.replace(/\D/g, '').slice(0, 14);
    return numbers
      .replace(/(\d{2})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1/$2')
      .replace(/(\d{4})(\d{1,2})/, '$1-$2');
  };

  // Formatar telefone: (00) 00000-0000
  const formatPhone = (value) => {
    const numbers = value.replace(/\D/g, '').slice(0, 11);
    if (numbers.length <= 10) {
      return numbers
        .replace(/(\d{2})(\d)/, '($1) $2')
        .replace(/(\d{4})(\d)/, '$1-$2');
    }
    return numbers
      .replace(/(\d{2})(\d)/, '($1) $2')
      .replace(/(\d{5})(\d)/, '$1-$2');
  };

  // Formatar CEP: 00000-000
  const formatCEP = (value) => {
    const numbers = value.replace(/\D/g, '').slice(0, 8);
    return numbers.replace(/(\d{5})(\d)/, '$1-$2');
  };

  // Buscar endereço pelo CEP
  const fetchAddressByCEP = async (cep) => {
    const cleanCEP = cep.replace(/\D/g, '');
    if (cleanCEP.length !== 8) return;
    
    try {
      const response = await fetch(`https://viacep.com.br/ws/${cleanCEP}/json/`);
      const data = await response.json();
      if (!data.erro) {
        setFormData(prev => ({
          ...prev,
          logradouro: data.logradouro || '',
          bairro: data.bairro || '',
          cidade: data.localidade || '',
          estado: data.uf || ''
        }));
      }
    } catch (err) {
      console.error('Erro ao buscar CEP:', err);
    }
  };

  // Validar formulário
  const validateForm = () => {
    if (personType === 'cpf') {
      if (!formData.cpf || formData.cpf.replace(/\D/g, '').length !== 11) {
        setError('CPF inválido');
        return false;
      }
      if (!formData.nome_completo || formData.nome_completo.length < 5) {
        setError('Nome completo é obrigatório');
        return false;
      }
      if (!formData.data_nascimento) {
        setError('Data de nascimento é obrigatória');
        return false;
      }
      const nascimento = new Date(formData.data_nascimento);
      const hoje = new Date();
      const idade = hoje.getFullYear() - nascimento.getFullYear() -
        (hoje < new Date(hoje.getFullYear(), nascimento.getMonth(), nascimento.getDate()) ? 1 : 0);
      if (idade < 18) {
        setError('É necessário ter 18 anos ou mais para realizar a compra.');
        return false;
      }
    } else {
      if (!formData.cnpj || formData.cnpj.replace(/\D/g, '').length !== 14) {
        setError('CNPJ inválido');
        return false;
      }
      if (!formData.razao_social || formData.razao_social.length < 3) {
        setError('Razão Social é obrigatória');
        return false;
      }
      if (!formData.nome_fantasia || formData.nome_fantasia.length < 2) {
        setError('Nome Fantasia é obrigatório');
        return false;
      }
      if (!formData.tipo_empresa) {
        setError('Selecione o tipo de empresa');
        return false;
      }
    }
    
    if (!formData.email || !formData.email.includes('@')) {
      setError('Email inválido');
      return false;
    }
    if (!formData.celular || formData.celular.replace(/\D/g, '').length < 10) {
      setError('Celular inválido');
      return false;
    }
    if (!formData.whatsapp || formData.whatsapp.replace(/\D/g, '').length < 10) {
      setError('WhatsApp inválido');
      return false;
    }
    
    return true;
  };

  // Enviar checkout
  const handleSubmit = async () => {
    if (!validateForm()) return;
    
    setIsLoading(true);
    setError('');
    setStep(3);
    
    try {
      const checkoutData = {
        person_type: personType,
        cpf: personType === 'cpf' ? formData.cpf.replace(/\D/g, '') : null,
        nome_completo: personType === 'cpf' ? formData.nome_completo : null,
        data_nascimento: personType === 'cpf' ? formData.data_nascimento : null,
        cnpj: personType === 'cnpj' ? formData.cnpj.replace(/\D/g, '') : null,
        razao_social: personType === 'cnpj' ? formData.razao_social : null,
        nome_fantasia: personType === 'cnpj' ? formData.nome_fantasia : null,
        tipo_empresa: personType === 'cnpj' ? formData.tipo_empresa : null,
        email: formData.email,
        celular: formData.celular.replace(/\D/g, ''),
        telefone: formData.telefone ? formData.telefone.replace(/\D/g, '') : null,
        whatsapp: formData.whatsapp.replace(/\D/g, ''),
        endereco: {
          cep: formData.cep.replace(/\D/g, ''),
          logradouro: formData.logradouro,
          numero: formData.numero,
          complemento: formData.complemento,
          bairro: formData.bairro,
          cidade: formData.cidade,
          estado: formData.estado
        },
        cart_items: cart.map(item => ({
          id: item.id,
          name: item.name,
          price: item.price,
          quantity: 1
        })),
        origin_url: window.location.origin
      };
      
      const response = await fetch(`${API_URL}/api/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(checkoutData)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Erro ao processar checkout');
      }
      
      const data = await response.json();
      
      if (data.checkout_url) {
        // Redirecionar para o Stripe Checkout
        window.location.href = data.checkout_url;
      } else {
        throw new Error('URL de pagamento não recebida');
      }
      
    } catch (err) {
      console.error('Erro no checkout:', err);
      setError(err.message || 'Erro ao processar pagamento. Tente novamente.');
      setStep(2);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm overflow-y-auto">
      <div className="bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl w-full max-w-2xl my-8 overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-cyan-600 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <CreditCard className="w-6 h-6 text-white" />
            <div>
              <h2 className="text-xl font-bold text-white">Finalizar Compra</h2>
              <p className="text-sm text-blue-100">Total: R$ {total.toFixed(2).replace('.', ',')}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-white/80 hover:text-white hover:bg-white/20 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Step 1: Escolher tipo de pessoa */}
        {step === 1 && (
          <div className="p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Como deseja se identificar?</h3>
            
            <div className="grid grid-cols-2 gap-4 mb-6">
              <button
                onClick={() => { setPersonType('cpf'); setStep(2); }}
                className="p-6 bg-slate-800 border-2 border-slate-700 hover:border-cyan-500 rounded-xl transition-all group"
              >
                <User className="w-10 h-10 text-cyan-400 mx-auto mb-3 group-hover:scale-110 transition-transform" />
                <h4 className="text-white font-semibold">Pessoa Física</h4>
                <p className="text-sm text-slate-400 mt-1">CPF</p>
              </button>
              
              <button
                onClick={() => { setPersonType('cnpj'); setStep(2); }}
                className="p-6 bg-slate-800 border-2 border-slate-700 hover:border-cyan-500 rounded-xl transition-all group"
              >
                <Building2 className="w-10 h-10 text-cyan-400 mx-auto mb-3 group-hover:scale-110 transition-transform" />
                <h4 className="text-white font-semibold">Pessoa Jurídica</h4>
                <p className="text-sm text-slate-400 mt-1">CNPJ</p>
              </button>
            </div>

            {/* Itens do carrinho resumo */}
            <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
              <h4 className="text-sm font-medium text-slate-400 mb-2">Itens selecionados:</h4>
              {cart.map((item, i) => (
                <div key={i} className="flex justify-between text-sm py-1">
                  <span className="text-white">{item.name}</span>
                  <span className="text-cyan-400">R$ {item.price.toFixed(2).replace('.', ',')}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Step 2: Formulário de dados */}
        {step === 2 && (
          <div className="p-6 max-h-[70vh] overflow-y-auto">
            {/* Aviso importante */}
            <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 mb-6 flex gap-3">
              <Info className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-blue-300 text-sm font-medium">Importante!</p>
                <p className="text-blue-200/80 text-xs mt-1">
                  Preencha corretamente seu <strong>email</strong> e <strong>WhatsApp</strong>. 
                  A licença será enviada por estes canais após a confirmação do pagamento.
                </p>
              </div>
            </div>

            {/* Botão voltar */}
            <button
              onClick={() => { setStep(1); setPersonType(''); setError(''); }}
              className="text-cyan-400 hover:text-cyan-300 text-sm mb-4 flex items-center gap-1"
            >
              ← Voltar
            </button>

            <h3 className="text-lg font-semibold text-white mb-4">
              {personType === 'cpf' ? 'Dados Pessoais' : 'Dados da Empresa'}
            </h3>

            {error && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 mb-4 flex items-center gap-2 text-red-400 text-sm">
                <AlertCircle className="w-4 h-4" />
                {error}
              </div>
            )}

            <div className="space-y-4">
              {/* CPF ou CNPJ */}
              {personType === 'cpf' ? (
                <>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-1">CPF *</label>
                    <input
                      type="text"
                      value={formData.cpf}
                      onChange={(e) => handleInputChange('cpf', formatCPF(e.target.value))}
                      placeholder="000.000.000-00"
                      className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-1">Nome Completo *</label>
                    <input
                      type="text"
                      value={formData.nome_completo}
                      onChange={(e) => handleInputChange('nome_completo', e.target.value)}
                      placeholder="Seu nome completo"
                      className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-1">Data de Nascimento * <span className="text-slate-500 font-normal">(obrigatório ser maior de 18 anos)</span></label>
                    <input
                      type="date"
                      value={formData.data_nascimento}
                      onChange={(e) => handleInputChange('data_nascimento', e.target.value)}
                      max={new Date(new Date().setFullYear(new Date().getFullYear() - 18)).toISOString().split('T')[0]}
                      className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500"
                    />
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-1">CNPJ *</label>
                    <input
                      type="text"
                      value={formData.cnpj}
                      onChange={(e) => handleInputChange('cnpj', formatCNPJ(e.target.value))}
                      placeholder="00.000.000/0000-00"
                      className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-1">Razão Social *</label>
                    <input
                      type="text"
                      value={formData.razao_social}
                      onChange={(e) => handleInputChange('razao_social', e.target.value)}
                      placeholder="Razão social da empresa"
                      className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-1">Nome Fantasia *</label>
                    <input
                      type="text"
                      value={formData.nome_fantasia}
                      onChange={(e) => handleInputChange('nome_fantasia', e.target.value)}
                      placeholder="Nome fantasia da empresa"
                      className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Tipo de Empresa *</label>
                    <div className="grid grid-cols-2 gap-2">
                      {tiposEmpresa.map(tipo => (
                        <button
                          key={tipo.id}
                          type="button"
                          onClick={() => handleInputChange('tipo_empresa', tipo.id)}
                          className={`p-3 rounded-lg border text-sm font-medium transition-all ${
                            formData.tipo_empresa === tipo.id
                              ? 'bg-cyan-500/20 border-cyan-500 text-cyan-400'
                              : 'bg-slate-800 border-slate-700 text-slate-300 hover:border-slate-600'
                          }`}
                        >
                          {tipo.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              )}

              {/* Contato */}
              <div className="border-t border-slate-700 pt-4 mt-4">
                <h4 className="text-white font-medium mb-3 flex items-center gap-2">
                  <Phone className="w-4 h-4 text-cyan-400" />
                  Contato
                </h4>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-1">Email *</label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      placeholder="seu@email.com"
                      className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-1">Celular *</label>
                    <input
                      type="text"
                      value={formData.celular}
                      onChange={(e) => handleInputChange('celular', formatPhone(e.target.value))}
                      placeholder="(00) 00000-0000"
                      className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-1">WhatsApp *</label>
                    <input
                      type="text"
                      value={formData.whatsapp}
                      onChange={(e) => handleInputChange('whatsapp', formatPhone(e.target.value))}
                      placeholder="(00) 00000-0000"
                      className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-1">Telefone Fixo</label>
                    <input
                      type="text"
                      value={formData.telefone}
                      onChange={(e) => handleInputChange('telefone', formatPhone(e.target.value))}
                      placeholder="(00) 0000-0000"
                      className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500"
                    />
                  </div>
                </div>
              </div>

              {/* Endereço */}
              <div className="border-t border-slate-700 pt-4 mt-4">
                <h4 className="text-white font-medium mb-3 flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-cyan-400" />
                  Endereço
                </h4>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-1">CEP</label>
                    <input
                      type="text"
                      value={formData.cep}
                      onChange={(e) => {
                        const cep = formatCEP(e.target.value);
                        handleInputChange('cep', cep);
                        if (cep.replace(/\D/g, '').length === 8) {
                          fetchAddressByCEP(cep);
                        }
                      }}
                      placeholder="00000-000"
                      className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-slate-300 mb-1">Logradouro</label>
                    <input
                      type="text"
                      value={formData.logradouro}
                      onChange={(e) => handleInputChange('logradouro', e.target.value)}
                      placeholder="Rua, Avenida..."
                      className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-1">Número</label>
                    <input
                      type="text"
                      value={formData.numero}
                      onChange={(e) => handleInputChange('numero', e.target.value)}
                      placeholder="Nº"
                      className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-slate-300 mb-1">Complemento</label>
                    <input
                      type="text"
                      value={formData.complemento}
                      onChange={(e) => handleInputChange('complemento', e.target.value)}
                      placeholder="Sala, Bloco..."
                      className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-1">Bairro</label>
                    <input
                      type="text"
                      value={formData.bairro}
                      onChange={(e) => handleInputChange('bairro', e.target.value)}
                      placeholder="Bairro"
                      className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-1">Cidade</label>
                    <input
                      type="text"
                      value={formData.cidade}
                      onChange={(e) => handleInputChange('cidade', e.target.value)}
                      placeholder="Cidade"
                      className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-1">Estado</label>
                    <input
                      type="text"
                      value={formData.estado}
                      onChange={(e) => handleInputChange('estado', e.target.value.toUpperCase().slice(0, 2))}
                      placeholder="UF"
                      maxLength={2}
                      className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500"
                    />
                  </div>
                </div>
              </div>

              {/* Resumo e Botão */}
              <div className="border-t border-slate-700 pt-4 mt-4">
                <div className="bg-slate-800/50 rounded-lg p-4 mb-4">
                  <div className="flex justify-between items-center">
                    <span className="text-slate-300">Total a pagar:</span>
                    <span className="text-2xl font-bold text-cyan-400">R$ {total.toFixed(2).replace('.', ',')}</span>
                  </div>
                </div>

                <Button
                  onClick={handleSubmit}
                  disabled={isLoading}
                  className="w-full bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white font-bold py-4 text-lg"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Processando...
                    </>
                  ) : (
                    <>
                      <CreditCard className="w-5 h-5 mr-2" />
                      Pagar com Cartão / PIX
                    </>
                  )}
                </Button>

                <p className="text-xs text-slate-500 text-center mt-3 flex items-center justify-center gap-1">
                  <CheckCircle2 className="w-3 h-3 text-green-400" />
                  Pagamento 100% seguro via Stripe
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Processando / Pagamento Iniciado */}
        {step === 3 && (
          <div className="p-8">
            {isLoading ? (
              <div className="text-center">
                <Loader2 className="w-16 h-16 text-cyan-400 animate-spin mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-white mb-2">Processando seu pedido...</h3>
                <p className="text-slate-400">Preparando o pagamento...</p>
              </div>
            ) : (
              <div className="text-center" data-testid="payment-initiated">
                <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle2 className="w-8 h-8 text-green-400" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">Pagamento iniciado!</h3>
                <p className="text-slate-400 mb-6">
                  Redirecionando para o pagamento seguro Stripe...<br />
                  Complete o pagamento na próxima tela.
                </p>
                <div className="bg-slate-800/50 rounded-lg p-4 mb-6 text-left">
                  <h4 className="text-sm font-medium text-slate-400 mb-2">Seu pacote:</h4>
                  {cart.map((item, i) => (
                    <div key={i} className="flex justify-between text-sm py-1">
                      <span className="text-white">{item.name}</span>
                      <span className="text-cyan-400">R$ {item.price.toFixed(2).replace('.', ',')}</span>
                    </div>
                  ))}
                  <div className="border-t border-slate-700 mt-2 pt-2 flex justify-between font-bold">
                    <span className="text-white">Total</span>
                    <span className="text-cyan-400">R$ {total.toFixed(2).replace('.', ',')}</span>
                  </div>
                </div>
                <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 mb-6 text-left">
                  <p className="text-blue-300 text-sm">
                    <strong>Após o pagamento:</strong> Você receberá um email de confirmação 
                    com os detalhes da sua compra e instruções para ativar sua licença.
                  </p>
                </div>
                <div className="space-y-3">
                  <button onClick={onClose} className="w-full bg-slate-700 hover:bg-slate-600 text-white font-medium py-3 rounded-lg transition-colors" data-testid="close-checkout-btn">
                    Fechar e Continuar Navegando
                  </button>
                  <a href="https://wa.me/5516991378312?text=Olá! Acabei de fazer um pagamento e preciso de ajuda."
                     target="_blank" rel="noopener noreferrer"
                     className="flex items-center justify-center gap-2 text-green-400 text-sm hover:text-green-300 transition-colors">
                    <MessageCircle className="w-4 h-4" /> Precisa de ajuda? Fale conosco
                  </a>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CheckoutModal;
